/**
 * Activity heuristics shared by the project track surfaces.
 *
 * ProjectHub and ProjectDetailPage both render "feel alive" copy
 * driven by the live ``Project`` fields (last_active_at,
 * overdue_tasks_count, …). Centralising the helpers here keeps the
 * voice consistent: the same heartbeat dot colour and the same
 * compact relative-time wording on both surfaces.
 *
 * The helpers stay deliberately Italian-only — the Hub greeting and
 * the Detail companion blurb both write in Italian today (see the
 * surrounding components). When EN ships, swap the literals through
 * Lingui ``msg`` like ``relativeDate.ts`` already does.
 */

/**
 * Compact italian relative-time formatter. Matches the ChatEmptyState
 * voice ("ora", "Nm fa", "Nh fa", "ieri", "Ng fa", "Nsett fa") and
 * falls back to a short ``dd MMM`` date for older points.
 */
export function formatShortRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "ora";
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.round(hours / 24);
  if (days === 1) return "ieri";
  if (days < 7) return `${days}g fa`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks}sett fa`;
  return new Date(t).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  });
}

/**
 * Forward-facing compact formatter ("tra Nm", "tra Nh", "domani",
 * "tra Ng"). Used by the automation / next-run callouts on the Hub
 * and Detail. Returns the same fallback ``dd MMM`` for points more
 * than ~5 weeks away.
 */
export function formatShortUntil(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diffMs = t - Date.now();
  if (diffMs <= 0) return "ora";
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `tra ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `tra ${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "domani";
  if (days < 7) return `tra ${days}g`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `tra ${weeks}sett`;
  return new Date(t).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  });
}

// ─── Activity classification (heartbeat dot) ──────────────────────────────

export type ActivityState = "active" | "warm" | "dormant" | "unknown";

/**
 * Bucket a project's ``last_active_at`` into a heartbeat colour:
 *   - ``active``  → green dot, edits within the last 3 days
 *   - ``warm``    → amber dot, edits within the last 2 weeks
 *   - ``dormant`` → grey  dot, older than that
 *   - ``unknown`` → very-light grey dot when the field is absent
 *
 * The dot colour map lives next to the classifier so a future tuning
 * (e.g. swapping the dormant cut-off) ships as a one-liner.
 */
export function classifyActivity(
  lastActiveAt: string | null | undefined,
): ActivityState {
  if (!lastActiveAt) return "unknown";
  const t = Date.parse(lastActiveAt);
  if (Number.isNaN(t)) return "unknown";
  const diffDays = (Date.now() - t) / (1000 * 60 * 60 * 24);
  if (diffDays < 3) return "active";
  if (diffDays < 14) return "warm";
  return "dormant";
}

export const ACTIVITY_DOT: Record<ActivityState, string> = {
  active: "bg-emerald-500",
  warm: "bg-amber-500",
  dormant: "bg-ink-3/40",
  unknown: "bg-ink-3/30",
};

export const ACTIVITY_LABEL: Record<ActivityState, string> = {
  active: "attivo",
  warm: "tiepido",
  dormant: "dormiente",
  unknown: "—",
};
